<script lang="ts">
  import { useThrelte } from '@threlte/core'
  import { useMouseEvents } from '../../hooks'
  import {
    Mesh,
    SphereGeometry,
    Vector3,
    Box3,
    Camera,
    Group,
    Object3D,
    Line,
    Quaternion,
    Matrix4,
    TubeGeometry,
    CatmullRomCurve3,
  } from 'three'
  import { cameraControls } from '../../stores/camera'
  import { get } from 'svelte/store'
  import { sceneMode } from '../../stores/scene-mode'
  import { getRaycasterElement } from '../../helpers/raycaster'
  import { canvasSelection, canvasMultiSelection } from '../../stores/canvas-selection'
  import {
    seamLineMaterial,
    seamMarkerHaloMaterial,
    seamMarkerMaterial,
    ThreeItemKeys,
    ThreeItemTypes,
  } from '../../constants/three'
  import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js'
  import useFileQueries from '../../routes/project/hooks/useFileQueries'
  import useCanvasSelection from '../../hooks/canvas-selection'

  const { scene, renderer } = useThrelte()
  const { replaceSeamMutation } = useFileQueries()
  const { hasSingleFile, hasSingleSoloGroup } = useCanvasSelection()

  const computeMeshSeamLinePoints = (mesh: Mesh, seamPoint: Vector3) => {
    const geom = BufferGeometryUtils.mergeVertices(mesh.geometry)

    const bbox = new Box3().setFromObject(mesh)
    const minZ = bbox.min.z
    const maxZ = bbox.max.z
    const step = (maxZ - minZ) / 200 // step in height
    const fillStep = 0.01 // controls density inside triangles

    const posAttr = geom.attributes.position
    const indexAttr = geom.index
    const points: Vector3[] = []

    if (!indexAttr) return []

    const a = new Vector3(),
      b = new Vector3(),
      c = new Vector3()

    for (let z = minZ; z <= maxZ; z += step) {
      const candidates: Vector3[] = []

      for (let i = 0; i < indexAttr.count; i += 3) {
        a.fromBufferAttribute(posAttr, indexAttr.getX(i)).applyMatrix4(
          mesh.matrixWorld,
        )
        b.fromBufferAttribute(posAttr, indexAttr.getX(i + 1)).applyMatrix4(
          mesh.matrixWorld,
        )
        c.fromBufferAttribute(posAttr, indexAttr.getX(i + 2)).applyMatrix4(
          mesh.matrixWorld,
        )

        const zMin = Math.min(a.z, b.z, c.z)
        const zMax = Math.max(a.z, b.z, c.z)
        if (z < zMin || z > zMax) continue // slice outside triangle

        // Interpolation of triangle intersection with the Z plane
        const edgePairs = [
          [a, b],
          [b, c],
          [c, a],
        ]
        const segPts: Vector3[] = []

        for (const [p1, p2] of edgePairs) {
          const dz1 = p1.z - z
          const dz2 = p2.z - z
          if ((dz1 > 0 && dz2 < 0) || (dz1 < 0 && dz2 > 0)) {
            const t = dz1 / (dz1 - dz2)
            const ix = p1.x + t * (p2.x - p1.x)
            const iy = p1.y + t * (p2.y - p1.y)
            segPts.push(new Vector3(ix, iy, z))
          }
        }

        if (segPts.length < 2) continue

        // Fill area between segment points across the triangle base
        const [p1, p2] = segPts
        const dir = new Vector3().subVectors(p2, p1)
        const len = dir.length()
        const nSteps = Math.max(2, Math.floor(len / fillStep))

        for (let t = 0; t <= 1; t += 1 / nSteps) {
          const pt = new Vector3(
            p1.x + t * (p2.x - p1.x),
            p1.y + t * (p2.y - p1.y),
            z,
          )
          candidates.push(pt)
        }
      }

      // Choose the point closest to seamPoint in XY plane
      if (candidates.length > 0) {
        let closest = candidates[0]
        let minDist = Infinity
        for (const p of candidates) {
          const dist = Math.hypot(p.x - seamPoint.x, p.y - seamPoint.y)
          if (dist < minDist) {
            minDist = dist
            closest = p
          }
        }
        points.push(closest)
      }
    }

    return points
  }

  const computeLineSeamLinePoints = (line: Line, seamPoint: Vector3) => {
    const geom = BufferGeometryUtils.mergeVertices(line.geometry)
    const posAttr = geom.getAttribute('position')

    if (!posAttr) return []

    const a = new Vector3()
    const b = new Vector3()
    const p = new Vector3()
    const dir = new Vector3()

    let closestPoint = new Vector3()
    let minDist = Infinity

    const toleranceM = 0.01

    for (let i = 0; i < posAttr.count - 1; i++) {
      a.fromBufferAttribute(posAttr, i).applyMatrix4(line.matrixWorld)
      if (i === posAttr.count - 2) {
        b.fromBufferAttribute(posAttr, 0).applyMatrix4(line.matrixWorld)
      }
      else {
        b.fromBufferAttribute(posAttr, i + 1).applyMatrix4(line.matrixWorld)
      }

      dir.subVectors(b, a)

      const len = dir.length()
      if (len === 0) continue

      const nbOfPoints = len / toleranceM

      for (let pointIndex = 0; pointIndex < nbOfPoints; pointIndex += 1) {
        p.lerpVectors(a, b, pointIndex / nbOfPoints)

        const dist = seamPoint.distanceTo(p)
        if (dist < minDist) {
          minDist = dist
          closestPoint.copy(p)
        }
      }
    }

    return [closestPoint]
  }

  const computeGroupSeamLinePoints = (group: Group, seamPoint: Vector3) => {
    let points: Vector3[] = []

    for (const child of group.children) {
      const newPoints = computeObjectSeamLinePoints(child, seamPoint)
      points = points.concat(newPoints)
    }

    return points
  }

  const computeObjectSeamLinePoints = (
    object: Object3D,
    seamPoint: Vector3,
  ) => {
    if (
      [
        ThreeItemTypes.SEAM,
        ThreeItemTypes.SEAM_LINE,
        ThreeItemTypes.SEAM_MARKER,
      ].includes(object.userData[ThreeItemKeys.ITEM_TYPE])
    ) {
      return []
    }

    switch (object.type) {
      case 'Mesh':
        return computeMeshSeamLinePoints(object as Mesh, seamPoint)
      case 'Group':
        return computeGroupSeamLinePoints(object as Group, seamPoint)
      case 'Line':
        return computeLineSeamLinePoints(object as Line, seamPoint)
      default:
        return []
    }
  }

  // --- Compute seam with BVH for accuracy ---
  const computeSeamLine = (object: Object3D, seamPoint: Vector3) => {
    let points = computeObjectSeamLinePoints(object, seamPoint)

    if (points.length > 1) {
      const curve = new CatmullRomCurve3(points)
      const tubeRadius = 0.01
      const tubeSegments = Math.max(8, points.length * 2)
      const radialSegments = 12
      const closed = false
      const tubeGeometry = new TubeGeometry(
        curve,
        tubeSegments,
        tubeRadius,
        radialSegments,
        closed,
      )
      const tubeMesh = new Mesh(tubeGeometry, seamLineMaterial)
      tubeMesh.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.SEAM_LINE
      return tubeMesh
    }
  }

  const rebaseToParent = (obj: Object3D, targetParent: Object3D) => {
    obj.updateMatrixWorld(true)
    targetParent.updateMatrixWorld(true)

    const world = obj.matrixWorld.clone()
    const invParent = new Matrix4().copy(targetParent.matrixWorld).invert()
    const local = new Matrix4().multiplyMatrices(invParent, world)

    obj.matrix.copy(local)
    obj.matrix.decompose(
      obj.position,
      obj.quaternion as Quaternion,
      obj.scale as Vector3,
    )
  }

  // Seam placement must stay unambiguous when several files overlap on screen
  // (e.g. closely stacked DXFs) - only the currently selected file is a valid
  // target, and it must be visible (hidden files can't be raycast-hit or edited).
  // A solo group (a group holding exactly one file) counts as that file: it's
  // presented in the left bar as if there were no group at all.
  const onClick = (e: MouseEvent) => {
    const selectedFile = get(canvasSelection)

    if (
      !selectedFile
      || get(canvasMultiSelection).length > 0
      || !(hasSingleFile() || hasSingleSoloGroup())
      || !selectedFile.visible
    ) {
      return
    }

    const intersect = getRaycasterElement(
      renderer,
      e,
      scene,
      get(cameraControls)?.camera as Camera,
      {
        intersectableItems: [ThreeItemTypes.FILE],
        ignoreLockedParents: true,
        roots: [selectedFile],
      },
    )
    if (intersect) {
      const seamPosition = intersect.intersection.point
      const seamGroup = new Group()

      const markerRadius = 0.03
      const markerGeom = new SphereGeometry(markerRadius, 24, 16)
      const marker = new Mesh(markerGeom, seamMarkerMaterial)
      marker.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.SEAM_MARKER
      marker.position.copy(seamPosition)
      seamGroup.add(marker)

      const haloGeom = new SphereGeometry(markerRadius * 1.6, 24, 16)
      const halo = new Mesh(haloGeom, seamMarkerHaloMaterial)
      halo.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.SEAM_MARKER
      halo.position.copy(seamPosition)
      seamGroup.add(halo)

      const seamLine = computeSeamLine(intersect.element, seamPosition)

      if (seamLine) {
        seamGroup.add(seamLine)
      }
      rebaseToParent(seamGroup, intersect.element)
      replaceSeamMutation.mutate({
        fileId: intersect.element.uuid,
        seamPosition,
        seamGroup,
      })
    }
  }

  // --- Reactivity binding ---
  $effect(() => {
    if ($sceneMode === 'seam') {
      return useMouseEvents({ onClick })
    }
  })
</script>
